import { ICellRendererParams } from 'ag-grid-enterprise'
import { Check, EyeOff, PencilLine, Timer } from 'lucide-react'

import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'
import { Audit } from '@fortem-monorepo/web-sdk'

import { useInternalUser } from '@/react-query/queries/account'

export const AuditCellRenderer = (params: ICellRendererParams<Audit.WithApprovalInfo>) => {
  const { internalUser } = useInternalUser()
  const status = params.data?.status
  const numRemainingApprovals =
    (params.data?.required_approvals ?? 0) - (params.data?.granted_approvals ?? 0)
  const wasLastUpdatedByMe = params.node.data?.submitted_by == internalUser?.user_id
  const isApproved = status === 'APPROVED'
  const canApprove = !isApproved && !wasLastUpdatedByMe

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={'flex items-center justify-center gap-2 w-full h-full'}>
          {status === null && (
            <Badge variant={'secondary'} theme="info">
              <EyeOff />
            </Badge>
          )}
          {status === 'DRAFT' && (
            <Badge variant={'secondary'} theme="destructive">
              <PencilLine />
              <span>
                {params.data?.granted_approvals}/{params.data?.required_approvals}
              </span>
            </Badge>
          )}
          {status === 'PENDING' && (
            <Badge variant={'secondary'} theme="warning">
              <Timer />
              <span>
                {params.data?.granted_approvals}/{params.data?.required_approvals}
              </span>
            </Badge>
          )}
          {status === 'APPROVED' && (
            <Badge variant={'secondary'} theme="success">
              <Check />
            </Badge>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className={'font-medium'}>{status === null ? 'UNTRACKED' : status}</p>
        {status === 'PENDING' && (
          <>
            <p className={'mt-2'}>
              {numRemainingApprovals} approval{numRemainingApprovals > 1 ? 's' : ''} remaining
            </p>
            {!canApprove && (
              <p>
                You are not authorized to approve this element, as you were the last person to
                create it.
              </p>
            )}
          </>
        )}
        {status === null && (
          <>
            <p className={'mt-2'}>
              You may approve this element. Ensure it is properly reviewed based on the nature of
              the element before proceeding.
            </p>
          </>
        )}
      </TooltipContent>
    </Tooltip>
  )
}
